import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Paywall } from "../Paywall";
import { Mixer } from "../mixer/Mixer";
import { InstrumentsPanel } from "../instruments/InstrumentsPanel";
import { LessonTools } from "./LessonTools";
import { useSpaceState } from "./useSpaceState";
import { useRecorder } from "./useRecorder";
import { InviteBanner, SpaceHeader, useClock } from "./SpaceHeader";
import { SpaceSidebar, Stage } from "./SpaceLayout";
import { mixOptions, useAudioRig, useInstrumentRouting, useLiveToken, useMixSync, usePlaybackSync } from "./useSpaceAudio";
import { errMsg, useApi } from "../../lib/api";

const SpaceError = ({ detail }) => (
  <div className="spotlight min-h-[70vh] grid place-items-center text-center px-6" data-testid="space-error">
    <div>
      <p className="text-xl font-bold">{typeof detail === "string" ? detail : "You can't open this space."}</p>
      <Link to="/" className="mt-4 inline-block text-yellow-300" data-testid="space-error-back">Back to the lobby</Link>
    </div>
  </div>
);

const reassign = (mix, trackId, idx) => ({
  ...mix,
  channels: mix.channels.map((c, i) => {
    if (i === idx) return { ...c, source: `t:${trackId}` };
    return c.source === `t:${trackId}` ? { ...c, source: null } : c;
  }),
});

// SpaceView is keyed by space id in App.js, so the audio rig is created once per space.
export default function SpaceView({ spaceId }) {
  const api = useApi();
  const { state, error, messages, setMessages, refresh } = useSpaceState(spaceId);
  const { engine, inst } = useAudioRig();
  const { lk, lkError } = useLiveToken(api, spaceId, state);
  const { mix, changeMix } = useMixSync(api, spaceId, engine, state?.mixer);
  usePlaybackSync(api, engine, state?.playback);
  const [localMic, setLocalMic] = useState(null);
  const [tracks, setTracks] = useState([]);
  const name = state?.kind === "room" ? state.space.name : state?.space.title;
  const rec = useRecorder({ spaceId, engine, localMic, title: name || "Bandcamp" });
  const now = useClock(!!state?.on_air || rec.recording);
  const canPlay = !!state && (state.kind === "lesson" || state.me.can_control || state.me.on_stage);
  useInstrumentRouting(engine, inst, state?.me.user_id, canPlay);

  if (error?.status === 402) return <Paywall detail={error.detail} />;
  if (error && !state) return <SpaceError detail={error.detail} />;
  if (!state || !mix) return <div className="min-h-[60vh] grid place-items-center mono-label animate-pulse" data-testid="space-loading">Opening the doors…</div>;

  const { me, kind, space } = state;
  const post = async (path, body) => { try { await api.post(path, body); await refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const myInstCh = mix.channels.findIndex((c) => c.source === `i:${me.user_id}`);

  return (
    <div className="spotlight min-h-[calc(100vh-56px)] px-4 md:px-6 py-6" data-testid={`space-view-${kind}`}>
      <div className="mx-auto max-w-[1600px]">
        <SpaceHeader state={state} spaceId={spaceId} name={name} engine={engine} rec={rec} now={now} post={post} refresh={refresh} />
        {me.invited && !me.on_stage && <InviteBanner spaceId={spaceId} post={post} />}
        <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_340px]">
          <div className="space-y-5 min-w-0">
            <section className="rounded-md border border-[#2d2d38] bg-black/50 p-3 md:p-4" data-testid="stage-area">
              <Stage lk={lk} lkError={lkError} engine={engine} inst={inst} canPlay={canPlay} state={state} setLocalMic={setLocalMic} />
            </section>
            {me.can_control && <Mixer engine={engine} mixer={mix} onChange={changeMix} options={mixOptions(state, mix, tracks)} />}
            <InstrumentsPanel inst={inst} canPlay={canPlay} liveConnected={!!lk}
              channelLabel={myInstCh >= 0 ? `CH ${myInstCh + 1}` : "the main mix"} />
            {kind === "lesson" && <LessonTools planId={space.lesson_plan_id} />}
          </div>
          <SpaceSidebar spaceId={spaceId} state={state} messages={messages} setMessages={setMessages} refresh={refresh}
            mix={mix} onAssign={(trackId, idx) => changeMix(reassign(mix, trackId, idx))} onTracks={setTracks} />
        </div>
      </div>
    </div>
  );
}
